/**
 * Engine-agnostic replacements for raw `Response.json()` in src/services.
 *
 * Why (#3546 / Sentry DALLALI-FRONTEND-GB): `Response.json()` on a non-JSON
 * body (proxy/CDN interstitial, gateway HTML error page, truncated payload —
 * common on flaky mobile Safari) throws an engine-specific SyntaxError
 * ("The string did not match the expected pattern." on JavaScriptCore) with a
 * native-only stack that can't be attributed to a call site. Reading the body
 * as text and parsing it ourselves turns that into a classified, descriptive
 * error — and normalizes the empty-body-on-2xx case to `undefined` the same
 * way nestRequest.ts does, without sniffing per-engine SyntaxError messages.
 *
 * The guard test (src/services/__tests__/safeJson.test.ts) fails any new raw
 * `.json()` call in this directory.
 */

/** A response whose body was non-empty but not parseable as JSON. */
export class NonJsonResponseError extends Error {
  readonly status: number;

  constructor(response: Response) {
    // Strip the query string: it can carry ids/tokens and would explode
    // Sentry grouping. An empty url (some test mocks) degrades gracefully.
    const path = response.url ? response.url.split('?')[0] : '<unknown url>';
    super(`Non-JSON response (HTTP ${response.status}) from ${path}`);
    this.name = 'NonJsonResponseError';
    this.status = response.status;
  }
}

/**
 * Parse a fetch Response body as JSON.
 * - Empty / whitespace-only body → `undefined` (mirrors nestRequest's 204-like
 *   normalization; #2897).
 * - Malformed body → NonJsonResponseError (descriptive, groupable) instead of
 *   an engine-specific native SyntaxError.
 *
 * `T` defaults to `any` deliberately: this is a drop-in for `response.json()`
 * (which returns `Promise<any>`), so swept call sites keep their existing
 * inference and casts unchanged.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function safeJson<T = any>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text.trim()) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new NonJsonResponseError(response);
  }
}

/**
 * Error-path variant: never throws. A non-JSON or empty error body yields `{}`
 * so the caller's HTTP-status-based error handling proceeds with its fallback
 * message instead of being masked by a parse failure.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function jsonOrEmpty<T = any>(response: Response): Promise<T> {
  try {
    return ((await safeJson(response)) ?? {}) as T;
  } catch {
    return {} as T;
  }
}
